<template>
  <div class="w-full">
    <DatatableReportV3
      id="customers-table"
      ref="customer_table"
      title="Customers"
      :config="config"
      :source="source"
      :filters="filters"
      no-download
      @column-searched="handleColumnSearched"
      @filter-applied="handleFilterApplied"
      @filter-removed="handleFilterRemoved"
      @filters-cleared="handleFiltersCleared"
    >
      <template #actions>
        <div class="flex flex-wrap gap-3">
          <MergeAccountsButton v-if="mergeEnabled" />
          <SeraButton
            icon="fa fa-plus fa-fw"
            text="Add Customer"
            class="md:whitespace-nowrap"
            @click="handleClick"
          />
        </div>
      </template>
    </DatatableReportV3>
    <ConfirmArchiveModal
      :open="confirmArchiveModalOpen"
      @archive-confirmed="handleArchiveConfirmed"
      @close="confirmArchiveModalOpen = false"
    />
    <ConfirmUnarchiveModal
      :open="confirmUnarchiveModalOpen"
      @close="confirmUnarchiveModalOpen = false"
    />
  </div>
</template>

<script>
import { customerClassificationColumns } from "@/lib/customerTypes";
import { EventBus } from "@/lib/event_bus.js";
import { trackMobileWebEvent } from "@/lib/pendo";

import SeraButton from "@/components/Button.vue";
import ConfirmArchiveModal from "@/Customer/ConfirmArchiveModal.vue";
import ConfirmUnarchiveModal from "@/Customer/ConfirmUnarchiveModal.vue";
import MergeAccountsButton from "@/Customer/MergeAccountsButton.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import TagList from "@/Tags/TagList.vue";
import store from "@/vuex/store";
import CustomerActionsMenu from "./CustomerActionsMenu.vue";

const CHOICE_FILTERS = ["archived", "customer_type"];

// On a phone the name must never collapse away, and the number a customer is
// called on sits under it, so neither costs a tap. The stacked copy is hidden
// by the style block below unless Responsive owns the table, where the Phone
// column is dropped (`never`) rather than shown twice.
function nameWithPhone(data, type, row) {
  if (type !== "display" || !row.phone) return data;

  return `${data}<div class="customer-phone-stack">${row.phone}</div>`;
}

export default {
  components: {
    ConfirmArchiveModal,
    ConfirmUnarchiveModal,
    DatatableReportV3,
    MergeAccountsButton,
    SeraButton
  },
  props: {
    source: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    }
  },
  data() {
    // The default sort is by position, so it moves with the classification
    // columns to stay on the same column.
    const classificationColumns = customerClassificationColumns();

    return {
      mergeEnabled: window.currentTenant.merge_tools_enabled,
      config: {
        responsive: true,
        pageLength: 25,
        order: [[1 + classificationColumns.length, "asc"]],
        columns: [
          { data: "id", orderable: false, responsivePriority: 3 },
          ...classificationColumns,
          {
            data: "name",
            className: "all wrap-when-responsive",
            responsivePriority: 1,
            render: nameWithPhone
          },
          {
            data: "address",
            orderable: false,
            className: "address-field",
            responsivePriority: 5
          },
          {
            data: "email",
            className: "email-field",
            responsivePriority: 4
          },
          {
            data: "phone",
            className: "phone-field never",
            responsivePriority: 2
          },
          {
            data: "tags",
            title: "Tags",
            orderable: false,
            searchable: false,
            responsivePriority: 6
          },
          {
            data: "date_added",
            title: "Date Added",
            searchable: false,
            responsivePriority: 7
          },
          {
            data: "actions",
            title: "",
            orderable: false,
            className: "text-center th-no-text",
            searchable: false
          }
        ],
        vueComponents: [
          {
            rootId: "customer-action-menu-vue",
            component: CustomerActionsMenu,
            options: {
              store
            }
          },
          {
            rootId: "tag-list-vue",
            component: TagList
          }
        ]
      },
      confirmArchiveModalOpen: false,
      confirmUnarchiveModalOpen: false,
      archiveCallback: () => {}
    };
  },
  mounted() {
    EventBus.$on("refresh-customer-table", this.refreshData);
    EventBus.$on("confirm-archive", this.handleConfirmArchive);
    EventBus.$on("confirm-unarchive", this.handleConfirmUnarchive);
  },
  beforeUnmount() {
    EventBus.$off("refresh-customer-table", this.refreshData);
    EventBus.$off("confirm-archive", this.handleConfirmArchive);
    EventBus.$off("confirm-unarchive", this.handleConfirmUnarchive);
  },
  methods: {
    // Customer lookup is one of the initiative's two target actions, and its
    // Pendo click tag cannot answer the mobile-share question — auto-captured
    // clicks carry no layout. Only the name column counts as a lookup, so it
    // also keeps its own event. Every search, name included, reports its column
    // so one event gives the share each column gets.
    handleColumnSearched({ column }) {
      trackMobileWebEvent("customer_column_searched", {
        surface: "customers_list",
        column
      });

      if (column !== "name") return;

      trackMobileWebEvent("customer_search", { surface: "customers_list" });
    },
    // Only the filter's name is sent, plus its value when that value is one of
    // a few fixed choices. Tag values are ids and say nothing in a chart.
    handleFilterApplied({ filter_name, filter_value, filter_value_count }) {
      trackMobileWebEvent("customer_filter_applied", {
        surface: "customers_list",
        filter_name,
        filter_value_count,
        ...(CHOICE_FILTERS.includes(filter_name) && { filter_value })
      });
    },
    handleFilterRemoved({ filter_name }) {
      trackMobileWebEvent("customer_filter_removed", {
        surface: "customers_list",
        filter_name
      });
    },
    handleFiltersCleared({ filters_cleared_count }) {
      trackMobileWebEvent("customer_filters_cleared", {
        surface: "customers_list",
        filters_cleared_count
      });
    },
    refreshData() {
      // Archiving/editing a customer refreshes the list in place. Keep the page
      // the user is on instead of snapping back to page 1 — they work through a
      // filtered list bottom-up, and a reset costs them the re-paginate.
      this.$refs.customer_table.refreshTable({ resetPaging: false });
    },
    handleClick() {
      if (this.$router) {
        this.$router.push("/customers/new");
      } else {
        window.location.href = this.userCreatePath();
      }
    },
    handleConfirmArchive(archiveCallback) {
      this.archiveCallback = archiveCallback;
      this.confirmArchiveModalOpen = true;
    },
    handleConfirmUnarchive() {
      this.confirmUnarchiveModalOpen = true;
    },
    handleArchiveConfirmed() {
      this.confirmArchiveModalOpen = false;
      this.archiveCallback();
    }
  }
};
</script>

<style lang="scss">
/* The cells are built by DataTables, outside this component's scope. */
#customers-table .customer-phone-stack {
  display: none;
}

#customers-table.dtr-column .customer-phone-stack {
  display: block;
  margin-top: 0.25rem;
}

/* A long word (a company name, a street) must wrap rather than set the
   narrowest the table can be, or the page scrolls sideways when zoomed. */
#customers-table.dtr-column td.wrap-when-responsive {
  overflow-wrap: anywhere;
}
</style>
